import { jsxs, jsx } from 'react/jsx-runtime';
import { createContext, useState, useId, useRef, useEffect, useLayoutEffect, useMemo, useContext } from 'react';

// src/utils/cx.ts
var collect = (value, out) => {
  if (!value) return;
  if (typeof value === "string" || typeof value === "number") {
    out.push(String(value));
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collect(item, out);
    return;
  }
  if (typeof value === "object") {
    for (const [key, enabled] of Object.entries(value)) {
      if (enabled) out.push(key);
    }
  }
};
var cx = (...values) => {
  const out = [];
  for (const v of values) collect(v, out);
  return out.join(" ");
};
var StatCard = ({
  ref,
  label,
  value,
  icon,
  tone = "default",
  className,
  ...rest
}) => /* @__PURE__ */ jsxs("div", { ref, className: cx("gm-statcard", `gm-statcard--${tone}`, className), ...rest, children: [
  icon ? /* @__PURE__ */ jsx("span", { className: "gm-statcard__icon", children: icon }) : null,
  /* @__PURE__ */ jsxs("div", { className: "gm-statcard__body", children: [
    /* @__PURE__ */ jsx("span", { className: "gm-statcard__label", children: label }),
    /* @__PURE__ */ jsx("span", { className: "gm-statcard__value", children: value })
  ] })
] });
StatCard.displayName = "StatCard";
var Badge = ({ ref, tone = "neutral", className, children, ...rest }) => /* @__PURE__ */ jsx("span", { ref, className: cx("gm-badge", `gm-badge--${tone}`, className), ...rest, children });
Badge.displayName = "Badge";
var StatusPill = ({
  ref,
  status,
  tone = "neutral",
  className,
  ...rest
}) => /* @__PURE__ */ jsxs(
  "span",
  {
    ref,
    className: cx("gm-badge", "gm-badge--dot", `gm-badge--${tone}`, className),
    ...rest,
    children: [
      /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "gm-badge__dot" }),
      status
    ]
  }
);
StatusPill.displayName = "StatusPill";
var PageHeader = ({
  ref,
  title,
  subtitle,
  actions,
  className,
  ...rest
}) => /* @__PURE__ */ jsxs("div", { ref, className: cx("gm-pageheader", className), ...rest, children: [
  /* @__PURE__ */ jsxs("div", { className: "gm-pageheader__titles", children: [
    /* @__PURE__ */ jsx("h1", { className: "gm-pageheader__title", children: title }),
    subtitle ? /* @__PURE__ */ jsx("p", { className: "gm-pageheader__subtitle", children: subtitle }) : null
  ] }),
  actions ? /* @__PURE__ */ jsx("div", { className: "gm-pageheader__actions", children: actions }) : null
] });
PageHeader.displayName = "PageHeader";
var EmptyState = ({
  ref,
  title,
  description,
  icon,
  action,
  className,
  ...rest
}) => /* @__PURE__ */ jsxs("div", { ref, className: cx("gm-emptystate", className), ...rest, children: [
  icon ? /* @__PURE__ */ jsx("div", { className: "gm-emptystate__icon", children: icon }) : null,
  /* @__PURE__ */ jsx("div", { className: "gm-emptystate__title", children: title }),
  description ? /* @__PURE__ */ jsx("p", { className: "gm-emptystate__description", children: description }) : null,
  action ? /* @__PURE__ */ jsx("div", { className: "gm-emptystate__action", children: action }) : null
] });
EmptyState.displayName = "EmptyState";
var toCss = (v) => typeof v === "number" ? `${v}px` : v;
var Skeleton = ({
  ref,
  width,
  height,
  radius: radius2,
  className,
  style,
  ...rest
}) => {
  const dims = {
    width: toCss(width),
    height: toCss(height ?? 16),
    borderRadius: toCss(radius2),
    ...style
  };
  return /* @__PURE__ */ jsx("div", { ref, className: cx("gm-skeleton", className), style: dims, ...rest });
};
Skeleton.displayName = "Skeleton";
var SkeletonText = ({ ref, lines = 3, className, ...rest }) => {
  const count = Math.max(1, lines);
  return /* @__PURE__ */ jsx("div", { ref, className: cx("gm-skeleton-text", className), ...rest, children: Array.from({ length: count }, (_, i) => /* @__PURE__ */ jsx(
    "div",
    {
      className: "gm-skeleton gm-skeleton-text__line",
      style: i === count - 1 && count > 1 ? { width: "60%" } : void 0
    },
    i
  )) });
};
SkeletonText.displayName = "SkeletonText";
var AppShell = ({ ref, sidebar, topbar, className, children, ...rest }) => /* @__PURE__ */ jsxs("div", { ref, className: cx("gm-appshell", className), ...rest, children: [
  /* @__PURE__ */ jsx("div", { className: "gm-appshell__sidebar", children: sidebar }),
  /* @__PURE__ */ jsx("div", { className: "gm-appshell__topbar", children: topbar }),
  /* @__PURE__ */ jsx("main", { className: "gm-appshell__content", children })
] });
AppShell.displayName = "AppShell";
var Sidebar = ({ ref, brand, className, children, ...rest }) => /* @__PURE__ */ jsxs("aside", { ref, className: cx("gm-sidebar", className), ...rest, children: [
  /* @__PURE__ */ jsx("div", { className: "gm-sidebar__brand", children: brand }),
  /* @__PURE__ */ jsx("nav", { className: "gm-sidebar__nav", children })
] });
Sidebar.displayName = "Sidebar";
var NavItem = ({ ref, label, icon, active, className, ...anchorProps }) => /* @__PURE__ */ jsxs(
  "a",
  {
    ref,
    "aria-current": active ? "page" : void 0,
    className: cx("gm-navitem", active ? "gm-navitem--active" : null, className),
    ...anchorProps,
    children: [
      icon ? /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "gm-navitem__icon", children: icon }) : null,
      /* @__PURE__ */ jsx("span", { className: "gm-navitem__label", children: label })
    ]
  }
);
NavItem.displayName = "NavItem";
var Topbar = ({
  ref,
  title,
  search,
  scope,
  user,
  className,
  children,
  ...rest
}) => /* @__PURE__ */ jsxs("header", { ref, className: cx("gm-topbar", className), ...rest, children: [
  /* @__PURE__ */ jsxs("div", { className: "gm-topbar__lead", children: [
    title ? /* @__PURE__ */ jsx("span", { className: "gm-topbar__title", children: title }) : null,
    search ? /* @__PURE__ */ jsx("div", { className: "gm-topbar__search", children: search }) : null,
    scope ? /* @__PURE__ */ jsx("div", { className: "gm-topbar__scope", children: scope }) : null,
    children
  ] }),
  /* @__PURE__ */ jsxs("div", { className: "gm-topbar__user", children: [
    user.email ? /* @__PURE__ */ jsx("span", { className: "gm-topbar__email", children: user.email }) : null,
    user.onSignOut ? /* @__PURE__ */ jsx("button", { type: "button", className: "gm-topbar__signout", onClick: user.onSignOut, children: "Sign out" }) : null
  ] })
] });
Topbar.displayName = "Topbar";
var alignClass = (align, part) => align && align !== "start" ? `gm-table__${part}--${align}` : null;
var DataTable = ({
  ref,
  columns,
  rows,
  getRowKey,
  onRowClick,
  isLoading,
  skeletonRows = 6,
  empty,
  className,
  ...rest
}) => {
  const colCount = columns.length;
  const clickable = onRowClick !== void 0;
  return /* @__PURE__ */ jsx("div", { className: "gm-table-wrap", children: /* @__PURE__ */ jsxs("table", { ref, className: cx("gm-table", className), ...rest, children: [
    /* @__PURE__ */ jsx("thead", { className: "gm-table__head", children: /* @__PURE__ */ jsx("tr", { children: columns.map((col) => /* @__PURE__ */ jsx(
      "th",
      {
        scope: "col",
        className: cx("gm-table__th", alignClass(col.align, "th")),
        style: col.width ? { width: col.width } : void 0,
        children: col.header
      },
      col.key
    )) }) }),
    /* @__PURE__ */ jsx("tbody", { className: "gm-table__body", children: isLoading ? Array.from({ length: Math.max(1, skeletonRows) }, (_, i) => /* @__PURE__ */ jsx(
      "tr",
      {
        className: "gm-table__row",
        children: columns.map((col) => /* @__PURE__ */ jsx("td", { className: cx("gm-table__td", alignClass(col.align, "td")), children: /* @__PURE__ */ jsx(Skeleton, { height: 14 }) }, col.key))
      },
      i
    )) : rows.length === 0 ? empty !== void 0 && /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { className: "gm-table__empty", colSpan: colCount, children: empty }) }) : rows.map((row) => {
      const onActivate = clickable ? () => onRowClick?.(row) : void 0;
      return /* @__PURE__ */ jsx(
        "tr",
        {
          className: cx("gm-table__row", clickable ? "gm-table__row--clickable" : null),
          ...clickable ? {
            tabIndex: 0,
            onClick: onActivate,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onActivate?.();
              }
            }
          } : {},
          children: columns.map((col) => /* @__PURE__ */ jsx(
            "td",
            {
              className: cx(
                "gm-table__td",
                alignClass(col.align, "td"),
                col.mono ? "gm-table__cell--mono" : null
              ),
              children: col.cell(row)
            },
            col.key
          ))
        },
        getRowKey(row)
      );
    }) })
  ] }) });
};
DataTable.displayName = "DataTable";
var Toolbar = ({ ref, lead, actions, className, children, ...rest }) => /* @__PURE__ */ jsxs("div", { ref, className: cx("gm-toolbar", className), ...rest, children: [
  lead ? /* @__PURE__ */ jsx("div", { className: "gm-toolbar__lead", children: lead }) : null,
  children ? /* @__PURE__ */ jsx("div", { className: "gm-toolbar__controls", children }) : null,
  actions ? /* @__PURE__ */ jsx("div", { className: "gm-toolbar__actions", children: actions }) : null
] });
Toolbar.displayName = "Toolbar";
var DetailHeader = ({
  ref,
  back,
  title,
  status,
  statusTone = "neutral",
  actions,
  meta,
  className,
  ...rest
}) => /* @__PURE__ */ jsxs("header", { ref, className: cx("gm-detailheader", className), ...rest, children: [
  back ? /* @__PURE__ */ jsx("div", { className: "gm-detailheader__back", children: back }) : null,
  /* @__PURE__ */ jsxs("div", { className: "gm-detailheader__bar", children: [
    /* @__PURE__ */ jsxs("div", { className: "gm-detailheader__heading", children: [
      /* @__PURE__ */ jsx("h1", { className: "gm-detailheader__title", children: title }),
      status ? /* @__PURE__ */ jsx(StatusPill, { status, tone: statusTone }) : null
    ] }),
    actions ? /* @__PURE__ */ jsx("div", { className: "gm-detailheader__actions", children: actions }) : null
  ] }),
  meta && meta.length > 0 ? /* @__PURE__ */ jsx("dl", { className: "gm-detailheader__meta", children: meta.map((item, i) => /* @__PURE__ */ jsxs(
    "div",
    {
      className: "gm-detailheader__meta-item",
      children: [
        /* @__PURE__ */ jsx("dt", { className: "gm-detailheader__meta-label", children: item.label }),
        /* @__PURE__ */ jsx(
          "dd",
          {
            className: cx(
              "gm-detailheader__meta-value",
              item.mono ? "gm-detailheader__meta-value--mono" : null
            ),
            children: item.value
          }
        )
      ]
    },
    i
  )) }) : null
] });
DetailHeader.displayName = "DetailHeader";
var Section = ({ ref, title, actions, className, children, ...rest }) => /* @__PURE__ */ jsxs("section", { ref, className: cx("gm-section", className), ...rest, children: [
  /* @__PURE__ */ jsxs("div", { className: "gm-section__head", children: [
    /* @__PURE__ */ jsx("h2", { className: "gm-section__title", children: title }),
    actions ? /* @__PURE__ */ jsx("div", { className: "gm-section__actions", children: actions }) : null
  ] }),
  /* @__PURE__ */ jsx("div", { className: "gm-section__body", children })
] });
Section.displayName = "Section";
var isEmpty = (value) => value === null || value === void 0 || value === "";
var DescriptionList = ({ ref, items, className, ...rest }) => /* @__PURE__ */ jsx("dl", { ref, className: cx("gm-desclist", className), ...rest, children: items.map((item) => /* @__PURE__ */ jsxs("div", { className: "gm-desclist__item", children: [
  /* @__PURE__ */ jsx("dt", { className: "gm-desclist__label", children: item.label }),
  /* @__PURE__ */ jsx("dd", { className: cx("gm-desclist__value", item.mono ? "gm-desclist__value--mono" : null), children: isEmpty(item.value) ? "\u2014" : item.value })
] }, item.label)) });
DescriptionList.displayName = "DescriptionList";
var EntityCard = ({
  ref,
  title,
  subtitle,
  trailing,
  className,
  ...rest
}) => /* @__PURE__ */ jsxs("div", { ref, className: cx("gm-entitycard", className), ...rest, children: [
  /* @__PURE__ */ jsxs("div", { className: "gm-entitycard__main", children: [
    /* @__PURE__ */ jsx("span", { className: "gm-entitycard__title", children: title }),
    subtitle ? /* @__PURE__ */ jsx("span", { className: "gm-entitycard__subtitle", children: subtitle }) : null
  ] }),
  trailing ? /* @__PURE__ */ jsx("div", { className: "gm-entitycard__trailing", children: trailing }) : null
] });
EntityCard.displayName = "EntityCard";
var Flag = ({ ref, code, title, className, ...rest }) => {
  const cc = code?.trim().toLowerCase();
  if (!cc || !/^[a-z]{2}$/.test(cc)) return null;
  return /* @__PURE__ */ jsx(
    "span",
    {
      ref,
      className: cx("fi", `fi-${cc}`, className),
      role: "img",
      "aria-label": title ?? cc.toUpperCase(),
      ...rest
    }
  );
};
Flag.displayName = "Flag";
function Combobox({
  ref,
  value,
  onValueChange,
  fetcher,
  getItemLabel,
  renderItem,
  onSelect,
  disabled = false,
  minChars = 2,
  debounceMs = 250,
  placeholder,
  inputSize = "sm"
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const ctrl = useRef(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  useEffect(() => {
    if (disabled || value.trim().length < minChars) {
      setItems([]);
      setOpen(false);
      return;
    }
    const t = setTimeout(() => {
      ctrl.current?.abort();
      const c = new AbortController();
      ctrl.current = c;
      setLoading(true);
      fetcherRef.current(value.trim(), c.signal).then((res) => {
        setItems(res);
        setOpen(true);
        setActive(0);
      }).catch(() => {
        if (!c.signal.aborted) setOpen(false);
      }).finally(() => setLoading(false));
    }, debounceMs);
    return () => clearTimeout(t);
  }, [value, disabled, minChars, debounceMs]);
  const choose = (item) => {
    onSelect(item);
    onValueChange(getItemLabel(item));
    setOpen(false);
  };
  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = items[active];
      if (it !== void 0) choose(it);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "gm-combobox", children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        ref,
        className: cx("gm-input", `gm-input--size-${inputSize}`),
        type: "text",
        role: "combobox",
        "aria-expanded": open,
        "aria-controls": listId,
        autoComplete: "off",
        value,
        placeholder,
        disabled: false,
        onChange: (e) => onValueChange(e.target.value),
        onKeyDown,
        onBlur: () => setTimeout(() => setOpen(false), 120)
      }
    ),
    loading ? /* @__PURE__ */ jsx("span", { className: "gm-combobox__hint", children: "Searching\u2026" }) : null,
    open && !disabled ? (
      // biome-ignore lint/a11y: ARIA 1.2 combobox popup — DOM focus stays on the input (role="combobox"); the listbox is virtually focused via the active option, so ul[role=listbox] is the canonical (non-focusable, non-semantic-HTML) structure.
      /* @__PURE__ */ jsx("ul", { className: "gm-combobox__list", id: listId, role: "listbox", children: items.length === 0 ? /* @__PURE__ */ jsx("li", { className: "gm-combobox__empty", children: "No match \u2014 press Enter to keep your text" }) : items.map((item, i) => (
        // biome-ignore lint/a11y: option rows are pointer affordances selected by mouse; keyboard nav (↑/↓/Enter/Esc) and DOM focus live on the combobox input, so options stay non-focusable.
        /* @__PURE__ */ jsx(
          "li",
          {
            role: "option",
            "aria-selected": i === active,
            className: cx("gm-combobox__option", i === active && "is-active"),
            onMouseDown: (e) => e.preventDefault(),
            onClick: () => choose(item),
            children: renderItem ? renderItem(item) : getItemLabel(item)
          },
          getItemLabel(item) + i
        )
      )) })
    ) : null
  ] });
}
Combobox.displayName = "Combobox";
var Button = ({
  ref,
  variant = "primary",
  size = "md",
  leadingIcon,
  trailingIcon,
  className,
  children,
  type = "button",
  ...rest
}) => /* @__PURE__ */ jsxs(
  "button",
  {
    ref,
    type,
    className: cx("gm-button", `gm-button--${variant}`, `gm-button--size-${size}`, className),
    ...rest,
    children: [
      leadingIcon ? /* @__PURE__ */ jsx("span", { className: "gm-button__icon gm-button__icon--leading", children: leadingIcon }) : null,
      /* @__PURE__ */ jsx("span", { className: "gm-button__label", children }),
      trailingIcon ? /* @__PURE__ */ jsx("span", { className: "gm-button__icon gm-button__icon--trailing", children: trailingIcon }) : null
    ]
  }
);
Button.displayName = "Button";
var Input = ({
  ref,
  inputSize = "md",
  invalid,
  className,
  type = "text",
  ...rest
}) => /* @__PURE__ */ jsx(
  "input",
  {
    ref,
    type,
    "aria-invalid": invalid || void 0,
    className: cx(
      "gm-input",
      `gm-input--size-${inputSize}`,
      invalid ? "gm-input--invalid" : null,
      className
    ),
    ...rest
  }
);
Input.displayName = "Input";
var Label = ({ ref, required, className, children, ...rest }) => (
  // biome-ignore lint/a11y/noLabelWithoutControl: Label is a polymorphic primitive; consumers wire it via `htmlFor` (matched to a controlled input's `id`) or by nesting a control inside. The rule cannot see either pattern through the props spread.
  /* @__PURE__ */ jsxs(
    "label",
    {
      ref,
      className: cx("gm-label", required ? "gm-label--required" : null, className),
      ...rest,
      children: [
        children,
        required ? /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "gm-label__required-indicator", children: "*" }) : null
      ]
    }
  )
);
Label.displayName = "Label";
var Stack = ({
  ref,
  direction = "column",
  align,
  justify,
  gap = "3",
  wrap,
  className,
  children,
  ...rest
}) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cx(
      "gm-stack",
      `gm-stack--${direction}`,
      align ? `gm-stack--align-${align}` : null,
      justify ? `gm-stack--justify-${justify}` : null,
      `gm-stack--gap-${gap}`,
      wrap ? "gm-stack--wrap" : null,
      className
    ),
    ...rest,
    children
  }
);
Stack.displayName = "Stack";
var Surface = ({
  ref,
  elevation = "0",
  tone = "canvas",
  bordered,
  className,
  children,
  ...rest
}) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cx(
      "gm-surface",
      `gm-surface--elevation-${elevation}`,
      `gm-surface--tone-${tone}`,
      bordered ? "gm-surface--bordered" : null,
      className
    ),
    ...rest,
    children
  }
);
Surface.displayName = "Surface";
var Text = ({
  ref,
  variant = "body-md",
  tone = "default",
  truncate,
  className,
  children,
  ...rest
}) => /* @__PURE__ */ jsx(
  "span",
  {
    ref,
    className: cx(
      "gm-text",
      `gm-text--${variant}`,
      `gm-text--tone-${tone}`,
      truncate ? "gm-text--truncate" : null,
      className
    ),
    ...rest,
    children
  }
);
Text.displayName = "Text";
var STORAGE_THEME_KEY = "gm:theme";
var STORAGE_DENSITY_KEY = "gm:density";
var ROOT_THEME_ATTR = "data-gm-theme";
var ROOT_DENSITY_ATTR = "data-gm-density";
var SYSTEM_QUERY = "(prefers-color-scheme: dark)";
var ThemeContext = createContext(null);
var isThemePreference = (value) => value === "light" || value === "dark" || value === "system";
var isDensityName = (value) => value === "comfortable" || value === "compact";
var systemTheme = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(SYSTEM_QUERY).matches ? "dark" : "light";
var readStoredPreference = (fallback) => {
  if (typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(STORAGE_THEME_KEY);
  return isThemePreference(stored) ? stored : fallback;
};
var readStoredDensity = (fallback) => {
  if (typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(STORAGE_DENSITY_KEY);
  if (isDensityName(stored)) return stored;
  return fallback;
};
var ThemeProvider = ({
  children,
  defaultTheme = "light",
  defaultDensity = "comfortable"
}) => {
  const [preference, setPreference] = useState(
    () => readStoredPreference(defaultTheme)
  );
  const [density, setDensity] = useState(() => readStoredDensity(defaultDensity));
  const [systemDark, setSystemDark] = useState(() => systemTheme() === "dark");
  const theme = preference === "system" ? systemDark ? "dark" : "light" : preference;
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(SYSTEM_QUERY);
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.setAttribute(ROOT_THEME_ATTR, theme);
    root.setAttribute(ROOT_DENSITY_ATTR, density);
    root.style.colorScheme = theme;
  }, [theme, density]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_THEME_KEY, preference);
  }, [preference]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_DENSITY_KEY, density);
  }, [density]);
  const value = useMemo(
    () => ({ theme, preference, density, setTheme: setPreference, setDensity }),
    [theme, preference, density]
  );
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value, children });
};
var useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside <ThemeProvider>");
  }
  return ctx;
};

// src/themes/light.ts
var lightThemeVars = {
  // brand scale (Evergreen) — light mode. Anchor = brand-9 (#356B50, RAM-OS --green-500).
  "--gm-color-brand-1": "#ECF1EE",
  "--gm-color-brand-2": "#DDE9E2",
  "--gm-color-brand-3": "#C9DBD0",
  "--gm-color-brand-4": "#B5CDC0",
  "--gm-color-brand-5": "#94B8A4",
  "--gm-color-brand-6": "#6E9C84",
  "--gm-color-brand-7": "#4E8568",
  "--gm-color-brand-8": "#3F7659",
  "--gm-color-brand-9": "#356B50",
  "--gm-color-brand-10": "#2C5A43",
  "--gm-color-brand-11": "#244836",
  "--gm-color-brand-12": "#152A20",
  // semantic — backgrounds (paper-warm canvas, white surfaces)
  "--gm-color-bg-base": "#EFEBE6",
  "--gm-color-bg-subtle": "#F7F4F0",
  "--gm-color-bg-raised": "#FFFFFF",
  "--gm-color-bg-overlay": "rgba(247, 244, 240, 0.92)",
  "--gm-color-bg-inverse": "#18191B",
  // semantic — foregrounds (ink text ramp)
  "--gm-color-fg-default": "#18191B",
  "--gm-color-fg-muted": "#6E6458",
  "--gm-color-fg-subtle": "#A99F92",
  "--gm-color-fg-on-accent": "#FFFFFF",
  "--gm-color-fg-on-inverse": "#F1EEEB",
  // semantic — borders (hairline warm-neutral)
  "--gm-color-border-subtle": "#EAE6E0",
  "--gm-color-border-default": "#DED9D1",
  "--gm-color-border-strong": "#CBC5BA",
  "--gm-color-border-focus": "#356B50",
  // accent (Evergreen — also = approved/go)
  "--gm-color-accent-bg": "#2C5A43",
  "--gm-color-accent-bg-hover": "#244836",
  "--gm-color-accent-bg-active": "#1D3A2C",
  "--gm-color-accent-fg": "#FFFFFF",
  "--gm-color-accent-border": "#356B50",
  // intents — success(green) warning(amber) danger(brick) info(slate-blue)
  "--gm-color-intent-success-bg": "#D7E5DD",
  "--gm-color-intent-success-fg": "#244836",
  "--gm-color-intent-success-border": "#B5CDC0",
  "--gm-color-intent-warning-bg": "#F0E6C8",
  "--gm-color-intent-warning-fg": "#836512",
  "--gm-color-intent-warning-border": "#E8D6A6",
  "--gm-color-intent-danger-bg": "#F1D8D2",
  "--gm-color-intent-danger-fg": "#97362B",
  "--gm-color-intent-danger-border": "#E3B3A9",
  "--gm-color-intent-info-bg": "#DEE5F0",
  "--gm-color-intent-info-fg": "#36507C",
  "--gm-color-intent-info-border": "#C2D0E4",
  // elevation — RAM-OS light (restrained, cool-tinted over hairline borders)
  "--gm-shadow-1": "0 1px 2px rgba(15, 36, 50, 0.06), 0 1px 1px rgba(15, 36, 50, 0.04)",
  "--gm-shadow-2": "0 2px 4px rgba(15, 36, 50, 0.06), 0 4px 12px rgba(15, 36, 50, 0.06)",
  "--gm-shadow-3": "0 4px 8px rgba(15, 36, 50, 0.06), 0 12px 28px rgba(15, 36, 50, 0.10)",
  "--gm-shadow-4": "0 8px 16px rgba(15, 36, 50, 0.08), 0 24px 48px rgba(15, 36, 50, 0.14)"
};

// src/themes/dark.ts
var darkThemeVars = {
  // brand scale (Evergreen) — dark mode. Anchor = brand-9 (#8DB29E), bright on ink.
  "--gm-color-brand-1": "#131E18",
  "--gm-color-brand-2": "#16261E",
  "--gm-color-brand-3": "#1D3A2C",
  "--gm-color-brand-4": "#244836",
  "--gm-color-brand-5": "#2C5A43",
  "--gm-color-brand-6": "#356B50",
  "--gm-color-brand-7": "#477E63",
  "--gm-color-brand-8": "#5E9277",
  "--gm-color-brand-9": "#8DB29E",
  "--gm-color-brand-10": "#B5CDC0",
  "--gm-color-brand-11": "#D7E5DD",
  "--gm-color-brand-12": "#ECF1EE",
  // semantic — backgrounds (ink canvas, raised charcoal surfaces)
  "--gm-color-bg-base": "#18191B",
  "--gm-color-bg-subtle": "#202123",
  "--gm-color-bg-raised": "#2A2B2D",
  "--gm-color-bg-overlay": "rgba(19, 20, 22, 0.92)",
  "--gm-color-bg-inverse": "#F1EEEB",
  // semantic — foregrounds (paper text ramp)
  "--gm-color-fg-default": "#F1EEEB",
  "--gm-color-fg-muted": "#908A80",
  "--gm-color-fg-subtle": "#6A655C",
  "--gm-color-fg-on-accent": "#18191B",
  "--gm-color-fg-on-inverse": "#18191B",
  // semantic — borders
  "--gm-color-border-subtle": "#2B2C2E",
  "--gm-color-border-default": "#343538",
  "--gm-color-border-strong": "#44454A",
  "--gm-color-border-focus": "#8DB29E",
  // accent (bright Evergreen on dark)
  "--gm-color-accent-bg": "#5E9277",
  "--gm-color-accent-bg-hover": "#8DB29E",
  "--gm-color-accent-bg-active": "#B5CDC0",
  "--gm-color-accent-fg": "#18191B",
  "--gm-color-accent-border": "#5E9277",
  // intents — dark (soft-tint bg, bright fg)
  "--gm-color-intent-success-bg": "#16271E",
  "--gm-color-intent-success-fg": "#8DB29E",
  "--gm-color-intent-success-border": "#285438",
  "--gm-color-intent-warning-bg": "#2C2718",
  "--gm-color-intent-warning-fg": "#DCC07A",
  "--gm-color-intent-warning-border": "#695119",
  "--gm-color-intent-danger-bg": "#2E1E1A",
  "--gm-color-intent-danger-fg": "#E0897B",
  "--gm-color-intent-danger-border": "#6E2A20",
  "--gm-color-intent-info-bg": "#1E2533",
  "--gm-color-intent-info-fg": "#9CB3D6",
  "--gm-color-intent-info-border": "#36507C",
  // elevation — dark (contrast does the lifting; shadows subtle)
  "--gm-shadow-1": "0 1px 2px rgba(0, 0, 0, 0.40)",
  "--gm-shadow-2": "0 2px 4px rgba(0, 0, 0, 0.44), 0 4px 8px rgba(0, 0, 0, 0.30)",
  "--gm-shadow-3": "0 4px 8px rgba(0, 0, 0, 0.48), 0 12px 28px rgba(0, 0, 0, 0.40)",
  "--gm-shadow-4": "0 8px 16px rgba(0, 0, 0, 0.52), 0 24px 48px rgba(0, 0, 0, 0.44)"
};

// src/themes/density.ts
var comfortableDensityVars = {
  "--gm-space-padding-inline-xs": "8px",
  "--gm-space-padding-inline-sm": "12px",
  "--gm-space-padding-inline-md": "16px",
  "--gm-space-padding-inline-lg": "24px",
  "--gm-space-padding-inline-xl": "32px",
  "--gm-space-padding-block-xs": "4px",
  "--gm-space-padding-block-sm": "8px",
  "--gm-space-padding-block-md": "12px",
  "--gm-space-padding-block-lg": "16px",
  "--gm-space-padding-block-xl": "24px",
  "--gm-space-gap-xs": "4px",
  "--gm-space-gap-sm": "8px",
  "--gm-space-gap-md": "12px",
  "--gm-space-gap-lg": "16px",
  "--gm-space-gap-xl": "24px"
};
var compactDensityVars = {
  "--gm-space-padding-inline-xs": "6px",
  "--gm-space-padding-inline-sm": "8px",
  "--gm-space-padding-inline-md": "12px",
  "--gm-space-padding-inline-lg": "16px",
  "--gm-space-padding-inline-xl": "24px",
  "--gm-space-padding-block-xs": "2px",
  "--gm-space-padding-block-sm": "4px",
  "--gm-space-padding-block-md": "8px",
  "--gm-space-padding-block-lg": "12px",
  "--gm-space-padding-block-xl": "16px",
  "--gm-space-gap-xs": "2px",
  "--gm-space-gap-sm": "4px",
  "--gm-space-gap-md": "8px",
  "--gm-space-gap-lg": "12px",
  "--gm-space-gap-xl": "16px"
};

// src/themes/css.ts
var themes = {
  light: lightThemeVars,
  dark: darkThemeVars
};
var densities = {
  comfortable: comfortableDensityVars,
  compact: compactDensityVars
};
var varsToInlineString = (vars) => Object.entries(vars).map(([name, value]) => `${name}: ${value};`).join(" ");
var buildThemeStylesheet = () => {
  const themeRules = Object.entries(themes).map(
    ([name, vars]) => `:where([data-gm-theme="${name}"]) { ${varsToInlineString(vars)} }`
  );
  const densityRules = Object.entries(densities).map(
    ([name, vars]) => `:where([data-gm-density="${name}"]) { ${varsToInlineString(vars)} }`
  );
  return [...themeRules, ...densityRules].join("\n");
};

// src/tokens/colors.ts
var scale = (name) => ({
  1: `var(--gm-color-${name}-1)`,
  2: `var(--gm-color-${name}-2)`,
  3: `var(--gm-color-${name}-3)`,
  4: `var(--gm-color-${name}-4)`,
  5: `var(--gm-color-${name}-5)`,
  6: `var(--gm-color-${name}-6)`,
  7: `var(--gm-color-${name}-7)`,
  8: `var(--gm-color-${name}-8)`,
  9: `var(--gm-color-${name}-9)`,
  10: `var(--gm-color-${name}-10)`,
  11: `var(--gm-color-${name}-11)`,
  12: `var(--gm-color-${name}-12)`
});
var colorScales = {
  brand: scale("brand"),
  sand: scale("sand"),
  tomato: scale("tomato"),
  amber: scale("amber"),
  grass: scale("grass"),
  blue: scale("blue")
};
var colorRoles = {
  bg: {
    base: "var(--gm-color-bg-base)",
    subtle: "var(--gm-color-bg-subtle)",
    raised: "var(--gm-color-bg-raised)",
    overlay: "var(--gm-color-bg-overlay)",
    inverse: "var(--gm-color-bg-inverse)"
  },
  fg: {
    default: "var(--gm-color-fg-default)",
    muted: "var(--gm-color-fg-muted)",
    subtle: "var(--gm-color-fg-subtle)",
    onAccent: "var(--gm-color-fg-on-accent)",
    onInverse: "var(--gm-color-fg-on-inverse)"
  },
  border: {
    subtle: "var(--gm-color-border-subtle)",
    default: "var(--gm-color-border-default)",
    strong: "var(--gm-color-border-strong)",
    focus: "var(--gm-color-border-focus)"
  },
  accent: {
    bg: "var(--gm-color-accent-bg)",
    bgHover: "var(--gm-color-accent-bg-hover)",
    bgActive: "var(--gm-color-accent-bg-active)",
    fg: "var(--gm-color-accent-fg)",
    border: "var(--gm-color-accent-border)"
  },
  intent: {
    success: {
      bg: "var(--gm-color-intent-success-bg)",
      fg: "var(--gm-color-intent-success-fg)",
      border: "var(--gm-color-intent-success-border)"
    },
    warning: {
      bg: "var(--gm-color-intent-warning-bg)",
      fg: "var(--gm-color-intent-warning-fg)",
      border: "var(--gm-color-intent-warning-border)"
    },
    danger: {
      bg: "var(--gm-color-intent-danger-bg)",
      fg: "var(--gm-color-intent-danger-fg)",
      border: "var(--gm-color-intent-danger-border)"
    },
    info: {
      bg: "var(--gm-color-intent-info-bg)",
      fg: "var(--gm-color-intent-info-fg)",
      border: "var(--gm-color-intent-info-border)"
    }
  }
};

// src/tokens/typography.ts
var fontFamily = {
  sans: '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace'
};
var fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700
};
var fontSize = {
  "3xs": "10px",
  "2xs": "11px",
  xs: "12px",
  sm: "13px",
  md: "14px",
  lg: "16px",
  xl: "18px",
  "2xl": "20px",
  "3xl": "24px",
  "4xl": "30px",
  "5xl": "36px",
  "6xl": "48px"
};
var lineHeight = {
  tight: "1.15",
  snug: "1.3",
  normal: "1.5",
  relaxed: "1.625"
};
var letterSpacing = {
  tight: "-0.02em",
  snug: "-0.01em",
  normal: "0",
  wide: "0.02em",
  wider: "0.06em"
};
var textStyles = {
  display: {
    fontSize: fontSize["5xl"],
    fontWeight: fontWeight.bold,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.tight,
    fontFamily: fontFamily.sans
  },
  heading1: {
    fontSize: fontSize["4xl"],
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.tight,
    fontFamily: fontFamily.sans
  },
  heading2: {
    fontSize: fontSize["3xl"],
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.snug,
    fontFamily: fontFamily.sans
  },
  heading3: {
    fontSize: fontSize["2xl"],
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
    fontFamily: fontFamily.sans
  },
  heading4: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
    fontFamily: fontFamily.sans
  },
  body: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
    fontFamily: fontFamily.sans
  },
  bodyStrong: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
    fontFamily: fontFamily.sans
  },
  caption: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
    fontFamily: fontFamily.sans
  },
  label: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
    fontFamily: fontFamily.sans
  },
  code: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
    fontFamily: fontFamily.mono
  },
  tableCell: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
    fontFamily: fontFamily.sans
  },
  tableHeader: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.wider,
    fontFamily: fontFamily.sans
  },
  badge: {
    fontSize: fontSize["2xs"],
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.wider,
    fontFamily: fontFamily.sans
  }
};

// src/tokens/spacing.ts
var space = {
  0: "0",
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  7: "28px",
  8: "32px",
  9: "36px",
  10: "40px",
  12: "48px",
  14: "56px",
  16: "64px",
  20: "80px",
  24: "96px",
  32: "128px",
  40: "160px",
  48: "192px",
  56: "224px",
  64: "256px"
};
var spacingAliases = {
  paddingInlineXs: "var(--gm-space-padding-inline-xs)",
  paddingInlineSm: "var(--gm-space-padding-inline-sm)",
  paddingInlineMd: "var(--gm-space-padding-inline-md)",
  paddingInlineLg: "var(--gm-space-padding-inline-lg)",
  paddingInlineXl: "var(--gm-space-padding-inline-xl)",
  paddingBlockXs: "var(--gm-space-padding-block-xs)",
  paddingBlockSm: "var(--gm-space-padding-block-sm)",
  paddingBlockMd: "var(--gm-space-padding-block-md)",
  paddingBlockLg: "var(--gm-space-padding-block-lg)",
  paddingBlockXl: "var(--gm-space-padding-block-xl)",
  gapXs: "var(--gm-space-gap-xs)",
  gapSm: "var(--gm-space-gap-sm)",
  gapMd: "var(--gm-space-gap-md)",
  gapLg: "var(--gm-space-gap-lg)",
  gapXl: "var(--gm-space-gap-xl)"
};

// src/tokens/radius.ts
var radius = {
  none: "0",
  xs: "2px",
  sm: "4px",
  md: "6px",
  lg: "8px",
  xl: "12px",
  "2xl": "16px",
  full: "9999px"
};

// src/tokens/shadow.ts
var shadow = {
  none: "none",
  1: "var(--gm-shadow-1)",
  2: "var(--gm-shadow-2)",
  3: "var(--gm-shadow-3)",
  4: "var(--gm-shadow-4)"
};
var shadowAliases = {
  raised: shadow[1],
  floating: shadow[2],
  overlay: shadow[3],
  popover: shadow[4]
};

// src/tokens/motion.ts
var duration = {
  instant: "0ms",
  fast: "120ms",
  base: "180ms",
  slow: "240ms",
  slower: "320ms"
};
var easing = {
  standard: "cubic-bezier(0.2, 0, 0, 1)",
  accelerate: "cubic-bezier(0.4, 0, 1, 1)",
  decelerate: "cubic-bezier(0, 0, 0.2, 1)",
  bouncy: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  linear: "linear"
};

// src/tokens/z-index.ts
var zIndex = {
  base: 0,
  raised: 10,
  dropdown: 100,
  sticky: 200,
  overlay: 300,
  modal: 400,
  popover: 500,
  toast: 600,
  tooltip: 700
};

// src/tokens/breakpoints.ts
var breakpoints = {
  xs: "480px",
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px"
};
var mediaDown = {
  xs: `(max-width: ${breakpoints.xs})`,
  sm: `(max-width: ${breakpoints.sm})`,
  md: `(max-width: ${breakpoints.md})`,
  lg: `(max-width: ${breakpoints.lg})`,
  xl: `(max-width: ${breakpoints.xl})`,
  "2xl": `(max-width: ${breakpoints["2xl"]})`
};

// src/utils/bootstrap-theme.ts
var DEFAULT_THEME_STORAGE_KEY = "gm:theme";
var DEFAULT_DENSITY_STORAGE_KEY = "gm:density";
var DEFAULT_THEME = "light";
var DEFAULT_DENSITY = "comfortable";
var getThemeBootstrapScript = (options = {}) => {
  const themeKey = options.themeStorageKey ?? DEFAULT_THEME_STORAGE_KEY;
  const densityKey = options.densityStorageKey ?? DEFAULT_DENSITY_STORAGE_KEY;
  const defaultTheme = options.defaultTheme ?? DEFAULT_THEME;
  const defaultDensity = options.defaultDensity ?? DEFAULT_DENSITY;
  return `(function(){try{var t=localStorage.getItem(${JSON.stringify(themeKey)});var d=localStorage.getItem(${JSON.stringify(densityKey)});var sys=window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light";var theme=t==="light"||t==="dark"?t:(t==="system"?sys:${JSON.stringify(defaultTheme)});var density=d==="comfortable"||d==="compact"?d:${JSON.stringify(defaultDensity)};var r=document.documentElement;r.setAttribute("data-gm-theme",theme);r.setAttribute("data-gm-density",density);r.style.colorScheme=theme;}catch(e){}})();`;
};

// src/index.ts
var VERSION = "0.1.0-build";

export { AppShell, Badge, Button, Combobox, DataTable, DescriptionList, DetailHeader, EmptyState, EntityCard, Flag, Input, Label, NavItem, PageHeader, Section, Sidebar, Skeleton, SkeletonText, Stack, StatCard, StatusPill, Surface, Text, ThemeProvider, Toolbar, Topbar, VERSION, breakpoints, buildThemeStylesheet, colorRoles, colorScales, comfortableDensityVars, compactDensityVars, cx, darkThemeVars, densities, duration, easing, fontFamily, fontSize, fontWeight, getThemeBootstrapScript, letterSpacing, lightThemeVars, lineHeight, mediaDown, radius, shadow, shadowAliases, space, spacingAliases, textStyles, themes, useTheme, varsToInlineString, zIndex };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map